import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField, Box } from "@mui/material";
import translations from "app/libs/i18n/translations.json";

const SECTOR_ORDER = ["construction", "architecture", "engineering", "industry"];

const candidateInputSx = {
  width: "100%",
  borderRadius: "12px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  bgcolor: "hsla(160, 10%, 96%, 0.3)",
  transition: "border-color 0.15s",
  "&:focus-within": { borderColor: "hsla(153, 68%, 25%, 0.4)" },
  "& .MuiAutocomplete-root": { width: "100%" },
  "& .MuiOutlinedInput-root": {
    height: "44px",
    padding: "0 12px !important",
    fontSize: "13px",
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(178, 93%, 10%)",
    bgcolor: "transparent",
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiInputBase-input::placeholder": {
    fontSize: "13px",
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(178, 10%, 45%)",
    opacity: 0.8,
  },
  "& .MuiAutocomplete-endAdornment": { display: "none" },
};

// Dropdown popup styled to match the CV-library region dropdown.
const dropdownPaperSx = {
  mt: "8px",
  borderRadius: "12px",
  border: "1px solid hsl(160, 10%, 90%)",
  boxShadow: "0 4px 12px hsla(178, 93%, 10%, 0.08)",
  "& .MuiAutocomplete-listbox": {
    p: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  "& .MuiAutocomplete-option": {
    borderRadius: "10px",
    minHeight: "auto",
    px: "12px !important",
    py: "8px !important",
    fontSize: "12px",
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(178, 10%, 45%)",
    "&.Mui-focused, &:hover": {
      bgcolor: "hsla(160, 10%, 96%, 0.7)",
      color: "hsl(178, 93%, 10%)",
    },
  },
  "& .MuiAutocomplete-groupLabel": {
    fontSize: "11px",
    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
    color: "hsl(178, 10%, 45%)",
    lineHeight: "28px",
    bgcolor: "transparent",
    px: "12px",
  },
};

const CertificateSelect = ({ selectedIds, label, disabled, onChange }) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [inputValue, setInputValue] = useState("");

  const localeTranslations = useMemo(
    () => translations[i18nLocale] || (i18nLocale === "fr-fr" ? translations.fr : translations.fr) || {},
    [i18nLocale]
  );

  const options = useMemo(() => {
    const sectorLabelKey = (sector) =>
      localeTranslations[`certificates_sector_label.${sector}`] ||
      sector.charAt(0).toUpperCase() + sector.slice(1);

    const built = [];
    SECTOR_ORDER.forEach((sector) => {
      Object.entries(localeTranslations)
        .filter(([key]) => key.startsWith(`certificates_list.${sector}.`))
        .sort(([, a], [, b]) => a.localeCompare(b))
        .forEach(([key, certLabel]) => {
          const id = key.split(".").pop();
          built.push({
            id,
            label: certLabel,
            category: sector,
            group: sectorLabelKey(sector),
          });
        });
    });
    return built;
  }, [localeTranslations]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <Box sx={candidateInputSx}>
      <Autocomplete
        disabled={disabled}
        options={options.filter((o) => !selectedSet.has(o.id))}
        groupBy={(option) => option.group}
        componentsProps={{ paper: { sx: dropdownPaperSx } }}
        getOptionLabel={(option) => option.label}
        value={null}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          if (reason !== "reset") setInputValue(newInputValue);
        }}
        onChange={(_, newValue) => {
          if (newValue) {
            onChange(newValue);
            setInputValue("");
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={label}
            onKeyDown={(e) => {
              if (e.key === "Backspace") e.stopPropagation();
            }}
          />
        )}
        renderTags={() => {}}
        blurOnSelect
        openOnFocus
        style={{ width: "100%" }}
      />
    </Box>
  );
};

export default CertificateSelect;
